import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Play, Pause, Loader2, Maximize2 } from 'lucide-react';
import { useAudio } from '../context/AudioContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmt = (s) => {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

/**
 * VideoPlayerModal — fullscreen overlay for a single video content item.
 *
 * Design notes:
 *   • Videos live in their own <video> element (they cannot share the
 *     global HTMLAudioElement). To keep only ONE media source audible,
 *     we call the central AudioContext.pause() when this modal opens,
 *     then never re-start audio on close (Rule: never autoplay).
 *   • Captions render via a <track> if `captionsUrl` is provided.
 *   • Playback progress is persisted to /api/listening/position using the
 *     same endpoint as audio, so Continue Listening works for videos too.
 *   • Never autoplays — the user must tap play (mobile browsers require
 *     this anyway, but we enforce it explicitly on desktop as well).
 */
const VideoPlayerModal = ({ open, item, onClose, initialPosition = 0 }) => {
  const { pause: pauseAudio, state: audioState } = useAudio();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const lastSaveRef = useRef({ t: 0, pos: 0 });

  const src = item?.videoUrl
    || (item?.videoAssetId ? `${API_URL}/api/media/${item.videoAssetId}` : null);
  const poster = item?.posterImageUrl || item?.coverImageUrl || '';
  const captionsSrc = item?.captionsUrl
    || (item?.captionsAssetId ? `${API_URL}/api/media/${item.captionsAssetId}` : null);

  // Pause any active audio when the modal opens — one media source at a time.
  useEffect(() => {
    if (!open) return;
    if (audioState?.playing) pauseAudio();
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Seek to saved position on load
  useEffect(() => {
    if (!open) return;
    const v = videoRef.current;
    if (!v) return;
    const onLoaded = () => {
      setDuration(v.duration || 0);
      setLoading(false);
      if (initialPosition && initialPosition > 0 && initialPosition < (v.duration || Infinity) - 5) {
        try { v.currentTime = initialPosition; } catch { /* noop */ }
      }
    };
    v.addEventListener('loadedmetadata', onLoaded);
    return () => v.removeEventListener('loadedmetadata', onLoaded);
  }, [open, initialPosition]);

  const persistPosition = (force = false) => {
    const v = videoRef.current;
    if (!v || !item?.id) return;
    const now = Date.now();
    const pos = v.currentTime || 0;
    if (!force && now - lastSaveRef.current.t < 15000 && Math.abs(pos - lastSaveRef.current.pos) < 15) return;
    lastSaveRef.current = { t: now, pos };
    const token = localStorage.getItem('rbr_token');
    if (!token) return;
    try {
      fetch(`${API_URL}/api/listening/position`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          contentId: item.id,
          position: pos,
          duration: v.duration || 0,
          sourceContext: 'video',
        }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* ignore */ }
  };

  const toggle = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      // Guard: pause any global audio at play-time too, in case user
      // resumed audio after opening the modal but before tapping play.
      if (audioState?.playing) pauseAudio();
      try { await v.play(); } catch (e) {
        setError('Tap play again to start');
      }
    } else {
      v.pause();
    }
  };

  const goFullscreen = () => {
    const v = videoRef.current;
    if (!v) return;
    const req = v.requestFullscreen || v.webkitEnterFullscreen || v.webkitRequestFullscreen;
    if (req) { try { req.call(v); } catch { /* ignore */ } }
  };

  const handleClose = () => {
    const v = videoRef.current;
    if (v) { try { v.pause(); } catch { /* ignore */ } }
    persistPosition(true);
    onClose?.();
  };

  if (!open || !item) return null;
  const pct = duration > 0 ? Math.min(100, (position / duration) * 100) : 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[95] flex flex-col animate-fade-in"
      data-testid="video-player-modal"
      style={{ background: '#0D2B24', color: '#F6F1E7' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-6 pb-3">
        <button
          onClick={handleClose}
          data-testid="video-player-close"
          aria-label="Close video"
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <p className="text-[10px] uppercase tracking-[0.22em] font-semibold truncate px-3" style={{ color: '#C79A4A' }}>
          {item.title || 'Video'}
        </p>
        <button
          onClick={goFullscreen}
          data-testid="video-player-fullscreen"
          aria-label="Fullscreen"
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-2">
        <div className="w-full max-w-[720px] aspect-video rounded-2xl overflow-hidden bg-black shadow-2xl relative">
          {src ? (
            <video
              ref={videoRef}
              data-testid="video-player-video"
              className="w-full h-full object-contain bg-black"
              poster={poster}
              controls
              controlsList="nodownload"
              playsInline
              preload="metadata"
              onPlay={() => { setPlaying(true); setError(null); }}
              onPause={() => { setPlaying(false); persistPosition(true); }}
              onTimeUpdate={() => {
                const v = videoRef.current; if (!v) return;
                setPosition(v.currentTime || 0);
                persistPosition(false);
              }}
              onWaiting={() => setLoading(true)}
              onPlaying={() => setLoading(false)}
              onError={() => { setError('Video could not load'); setLoading(false); }}
              onEnded={() => { persistPosition(true); setPlaying(false); }}
            >
              <source src={src} type="video/mp4" />
              {captionsSrc && (
                <track kind="captions" src={captionsSrc} srcLang="en" label="English" default />
              )}
            </video>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">
              No video source
            </div>
          )}
          {loading && !error && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Loader2 className="w-8 h-8 animate-spin text-white/70" />
            </div>
          )}
        </div>

        <div className="w-full max-w-[720px] mt-4 px-1">
          <p className="text-lg leading-tight" style={{ fontFamily: "'DM Serif Display', 'Playfair Display', serif" }} data-testid="video-player-title">
            {item.title}
          </p>
          {item.shortDescription && (
            <p className="text-[13px] mt-1" style={{ color: 'rgba(246,241,231,0.65)' }}>
              {item.shortDescription}
            </p>
          )}
          {error && <p className="text-[12px] mt-2" style={{ color: '#E4A79E' }}>{error}</p>}

          <div className="flex items-center gap-4 mt-4">
            <button
              onClick={toggle}
              data-testid="video-player-toggle"
              className="w-14 h-14 rounded-full flex items-center justify-center transition-transform active:scale-95 shadow-2xl"
              style={{ background: '#C79A4A' }}
              aria-label={playing ? 'Pause' : 'Play'}
            >
              {playing ? <Pause className="w-6 h-6" style={{ color: '#0D2B24' }} /> : <Play className="w-6 h-6 ml-0.5" style={{ color: '#0D2B24' }} />}
            </button>
            <div className="flex-1">
              <div className="h-1.5 bg-white/15 rounded-full overflow-hidden">
                <div className="h-full transition-[width] duration-150" style={{ width: `${pct}%`, background: '#C79A4A' }} />
              </div>
              <div className="flex justify-between text-[11px] mt-1" style={{ color: 'rgba(246,241,231,0.65)' }}>
                <span>{fmt(position)}</span>
                <span>{fmt(duration)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default VideoPlayerModal;
